import { FC, useEffect, useState } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { Checklist } from '@eneco/components/ui/Checklist/Checklist';
import useDC from '@eneco/dc/useDC';
import { FlowError, Header, Layout, NextButton } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { useFormatter } from '@eneco/i18n';
import {
  Box,
  Card,
  Heading,
  NotificationBox,
  Stack,
  Badge,
  Text,
  ToggleSwitch,
  TextLink,
} from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { Co2UpsellStepRendering } from '@sitecore/types/Co2UpsellStep';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';

import { FlowConsumerContextEnergy } from '../../../context';
import { getOffer, getOfferAlternative, getUpsellProductPrice } from '../../../shared/helpers';
import { useFlowTracking, useFlowNavigation, useCachedOfferMachine } from '../../../shared/hooks';

interface FormFields {
  hasUpsellGas: boolean;
}

export const SustainableChoiceStep: FC = () => {
  const { businessUnit, label } = useDC();
  const { useFlowSelector } = useFlowHooks<FlowConsumerContextEnergy>();
  const flowState = useFlowSelector(state => state);
  const { updateContextAndGoToNextStep } = useFlowNavigation();

  const {
    context: flowContext,
    context: { hasUpsellGas },
  } = flowState;

  const { offerState, sendToOfferMachine } = useCachedOfferMachine(businessUnit, label);

  const offer = offerState?.context?.offer;

  const { Co2UpsellStep: { fields: textualData } = {} } = usePlaceholderContent<{
    Co2UpsellStep: Co2UpsellStepRendering;
  }>();

  const formSchema = yup.object({
    hasUpsellGas: yup.boolean(),
  });

  const {
    handleSubmit,
    control,
    getValues,
    watch,
    formState: { isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onSubmit',
    defaultValues: {
      hasUpsellGas: !!hasUpsellGas,
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  const { trackAddToCart, trackRemoveFromCart } = useFlowTracking();

  const mutatedHasUpsellGas = watch('hasUpsellGas');

  const selectedOffer = getOffer(flowContext, offer);
  const availableOffer = getOfferAlternative({ ...flowContext, hasUpsellGas: mutatedHasUpsellGas }, offer);

  const priceUpsellGas = getUpsellProductPrice('gas', flowContext, offer);

  const { currency } = useFormatter();

  const [isLoading, setIsLoading] = useState(true);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isFailed, setIsFailed] = useState(false);

  const [isNotFound, setIsNotFound] = useState(false);
  const [isNotAvailable, setIsNotAvailable] = useState(false);

  useEffect(() => {
    sendToOfferMachine({
      type: 'GET_OFFER',
      flowContext: { ...flowContext },
    });
  }, []);

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formValues = getValues();

      if (formValues.hasUpsellGas !== hasUpsellGas) {
        if (selectedOffer) trackRemoveFromCart(selectedOffer, offer);
        if (availableOffer) trackAddToCart(availableOffer, offer);
      }

      updateContextAndGoToNextStep({
        ...formValues,
        ...(formValues.hasUpsellGas !== hasUpsellGas && { discountCode: availableOffer?.type }),
      });
    }
  }, [isSubmitSuccessful]);

  useEffect(() => {
    if (!offerState.matches('IDLE')) {
      setIsLoading(offerState.matches('FETCHING'));
      setIsFailed(!offerState.matches('FETCHING') && !offerState.matches('SUCCESS'));
      setIsSuccessful(offerState.matches('SUCCESS'));

      setIsNotFound(offerState.matches('ERROR_NOT_FOUND'));
      setIsNotAvailable(offerState.matches('ERROR_NOT_AVAILABLE'));
    }
  }, [offerState]);

  const getErrorNotification = () => {
    if (isNotAvailable) return textualData?.genericError?.errorNotAvailableNotification;
    if (isNotFound) return textualData?.genericError?.errorNotFoundNotification;

    return textualData?.genericError?.errorNotification;
  };

  return (
    <Layout.Content variant="A" isLoading={isLoading} handleSubmit={handleSubmit}>
      {isSuccessful && (
        <>
          <Header>
            <Header.Title>{textualData?.content?.title?.value}</Header.Title>
            {textualData?.content?.text?.value && <Header.Text>{textualData.content.text.value}</Header.Text>}
          </Header>

          <Stack direction="column" gap="5">
            {textualData?.content?.notification?.value?.content && (
              <Box paddingBottom={5}>
                <NotificationBox
                  isAlert={false}
                  variant={textualData?.content?.notification?.value?.variant}
                  title={textualData?.content?.notification?.value?.title}
                  text={<RichText html={textualData?.content?.notification?.value?.content} />}
                />
              </Box>
            )}

            <Card corners="rounded">
              <Card.Content>
                <Heading as="h2" size="XS" id="upsell-gas-heading">
                  {textualData?.data?.name?.value}
                </Heading>

                <Box paddingY="3">
                  {textualData?.data?.promotionText?.value && <Badge>{textualData?.data?.promotionText?.value}</Badge>}
                </Box>

                <Box paddingY="3">
                  <Text size="BodyXL" weight="bold">
                    {currency.euro(priceUpsellGas ?? 0)}{' '}
                    <Text size="BodyS" color="textLowEmphasis" weight="regular">
                      <RichText html={textualData?.data?.byPeriodLabel?.value} />
                    </Text>
                  </Text>
                </Box>

                <Checklist>
                  <Checklist.Item key={1} gap="2">
                    {textualData?.data?.usp1Text?.value && <RichText html={textualData?.data?.usp1Text?.value} />}
                  </Checklist.Item>
                  <Checklist.Item key={2} gap="2">
                    {textualData?.data?.usp2Text?.value && <RichText html={textualData?.data?.usp2Text?.value} />}
                  </Checklist.Item>
                </Checklist>

                <Box paddingY="3">
                  <Controller
                    control={control}
                    name="hasUpsellGas"
                    render={({ field: { onChange, value, name, ref } }) => (
                      <ToggleSwitch
                        aria-labelledby="upsell-gas-heading"
                        onCheckedChange={onChange}
                        isChecked={value}
                        name={name}
                        ref={ref}
                      />
                    )}
                  />
                </Box>
              </Card.Content>
            </Card>

            {textualData?.content?.moreInfoDialog?.value?.triggerText && (
              <TrackedDialog
                title={textualData?.content?.moreInfoDialog?.value?.title}
                trigger={
                  <TextLink emphasis="high">{textualData?.content?.moreInfoDialog?.value?.triggerText}</TextLink>
                }>
                <RichText html={textualData?.content?.moreInfoDialog?.value?.content} />
              </TrackedDialog>
            )}

            <Text size="BodyS" color="textLowEmphasis" weight="regular">
              {textualData?.content?.footnoteText?.value && (
                <RichText html={textualData?.content?.footnoteText?.value} />
              )}
            </Text>

            <Box paddingTop={3}>
              <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>
            </Box>
          </Stack>
        </>
      )}

      {isFailed && (
        <FlowError
          textualData={{
            notification: getErrorNotification(),
            buttonLabel: !isNotAvailable && !isNotFound ? textualData?.genericError?.tryAgainButtonText : undefined,
          }}
        />
      )}
    </Layout.Content>
  );
};
